import { useMutation } from "@apollo/client";
import {
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useLocaleState, useNotify, useTranslate } from "react-admin";

import {
  CancelStaffInvitationDocument,
  type CancelStaffInvitationMutation,
  type CancelStaffInvitationMutationVariables,
  StaffInvitationsDocument,
  type StaffInvitationsQuery,
  StaffInvitationStatus,
} from "../../generated/graphql";
import { formatDateTimeInline } from "../../lib/dateUtils";
import { captureException } from "../../lib/errorHandling";
import { staffManagementErrorKey } from "../../lib/staffManagement";
import { color, shadows } from "../../theme/designTokens";
import { CustomConfirm } from "../CustomConfirm";

type StaffInvitation = StaffInvitationsQuery["staffInvitations"][number];

type StaffInvitationsTableProps = {
  invitations: StaffInvitationsQuery["staffInvitations"];
};

export const StaffInvitationsTable = ({ invitations }: StaffInvitationsTableProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const [pendingCancel, setPendingCancel] = useState<StaffInvitation | null>(null);
  const [cancelInvitation, { loading }] = useMutation<
    CancelStaffInvitationMutation,
    CancelStaffInvitationMutationVariables
  >(CancelStaffInvitationDocument);

  const handleCancel = async ({ id, email }: StaffInvitation) => {
    setPendingCancel(null);
    try {
      await cancelInvitation({
        variables: { id },
        refetchQueries: [{ query: StaffInvitationsDocument }],
        awaitRefetchQueries: true,
      });
      notify(translate("team.invitations.cancelled", { email }), { type: "success" });
    } catch (error) {
      const key = staffManagementErrorKey(error);
      if (key === null) captureException(error, "staff/team: cancel invitation");
      notify(translate(key ?? "team.invitations.cancelError"), { type: "error" });
    }
  };

  if (invitations.length === 0) {
    return <Typography color="text.secondary">{translate("team.invitations.empty")}</Typography>;
  }

  return (
    <>
      <Paper variant="outlined" sx={{ borderColor: color.panelBorder, boxShadow: shadows.card }}>
        <TableContainer>
          <Table size="small" data-testid="staff-invitations">
            <TableHead>
              <TableRow>
                <TableCell>{translate("team.invitations.columns.email")}</TableCell>
                <TableCell>{translate("team.invitations.columns.roles")}</TableCell>
                <TableCell>{translate("team.invitations.columns.status")}</TableCell>
                <TableCell>{translate("team.invitations.columns.invitedBy")}</TableCell>
                <TableCell>{translate("team.invitations.columns.createdAt")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map(invitation => (
                <TableRow key={invitation.id} data-testid="staff-invitation-row">
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                      {invitation.roles.map(role => (
                        <Chip key={role} size="small" label={translate(`staffRoles.${role}`)} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={invitation.status === StaffInvitationStatus.Failed ? "error" : undefined}
                    >
                      {translate(`team.invitations.status.${invitation.status}`)}
                    </Typography>
                  </TableCell>
                  <TableCell>{invitation.invitedByName ?? "–"}</TableCell>
                  <TableCell>{formatDateTimeInline(invitation.createdAt, locale)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      disabled={loading}
                      aria-label={translate("team.invitations.cancelFor", { email: invitation.email })}
                      onClick={() => setPendingCancel(invitation)}
                    >
                      {translate("team.invitations.cancel")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CustomConfirm
        isOpen={pendingCancel !== null}
        title="team.invitations.cancelTitle"
        content={pendingCancel ? translate("team.invitations.cancelConfirm", { email: pendingCancel.email }) : ""}
        onConfirm={() => pendingCancel && handleCancel(pendingCancel)}
        onClose={() => setPendingCancel(null)}
      />
    </>
  );
};
